// Pitch 8, "shareable fights": what `?fight=<record>` opens onto instead of the ordinary stage machinery.
// A small landing card while the area of operations reloads and the order log replays in the background
// (src/sim/replayClient.ts) to verify the record reproduces its own hash, then two chips: watch the
// fight happen (a headless replay straight to the debrief) or pick up the same plan and fight it
// yourself. See `src/share/fight.ts` for the record shape and `docs/planning/2026-09-21-pitches.md`
// pitch 8.

import { useEffect, useRef, useState } from 'react';
import { useStore } from '../../state/store';
import { useDebrief } from '../../state/debrief';
import { decodeFight, type FightRecord } from '../../share/fight';
import { runReplay, type ReplayResult } from '../../sim/replayClient';
import { cachePut, setRuntimeCacheMode } from '../../ai/cache';
import { Wordmark } from '../Wordmark';

type Verify = 'checking' | 'identical' | 'diverged';

/** Strips `?fight=...` from the address bar once the record has been read, so a reload (or the browser's
 * own back/forward) does not reopen this card over whatever the player did next. */
function clearFightParam(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete('fight');
  window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
}

export function SharedFightLanding({ token, onResolved }: { token: string; onResolved: () => void }) {
  const [record, setRecord] = useState<FightRecord | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [verify, setVerify] = useState<Verify>('checking');
  const [replay, setReplay] = useState<ReplayResult | null>(null);
  const [busy, setBusy] = useState<'watch' | 'plan' | null>(null);
  const started = useRef(false);

  useEffect(() => {
    // `started` (not a `cancelled`-flag cleanup) is the guard: React 18 StrictMode's development-only
    // double-invoke runs this effect, its cleanup, then this effect again on the same component instance
    // before the first async chain below ever resolves - a `cancelled` flag set by that cleanup would
    // abort the one run that was actually going to finish, and the second invocation would then no-op
    // forever (this landing card sat on "WINDTUNNEL shared fight" with nothing else rendering, no error,
    // no console output - exactly what that race looks like). This card's whole job is to run once and
    // land on a result; nothing here should be interrupted by StrictMode's rehearsal of unmounting.
    if (started.current) return;
    started.current = true;

    (async () => {
      let rec: FightRecord;
      try {
        rec = await decodeFight(token);
      } catch {
        setLoadError('This link is not a valid fight — the URL may be truncated or damaged.');
        return;
      }
      setRecord(rec);

      // Cache exactness (item 4): seed this tab's cache with the sharer's recorded answers, and prefer
      // reading it over the network for the rest of this session, before anything narrates.
      if (rec.llm) {
        for (const [key, entry] of Object.entries(rec.llm)) cachePut(key, entry);
      }
      setRuntimeCacheMode('read');

      try {
        await useStore.getState().loadSharedScenario(rec.ao, rec.seed, rec.dials, rec.drawnGraphics);
        const { meta, buffers, scenario } = useStore.getState();
        if (!meta || !buffers || !scenario) throw new Error('area of operations failed to load');
        const result = await runReplay({
          meta, buffers, scenario, dials: rec.dials, seed: rec.seed,
          orderLog: rec.orderLog, untilTick: rec.endedTick,
        });
        setReplay(result);
        setVerify(result.hash === rec.hash ? 'identical' : 'diverged');
      } catch (e) {
        setVerify('diverged');
        setLoadError(e instanceof Error ? e.message : String(e));
      }
    })();
  }, [token]);

  function watchReplay(): void {
    if (!record || !replay || busy) return;
    setBusy('watch');
    useDebrief.getState().capture({
      snap: replay.snap, events: replay.events, truth: replay.truth,
      orderLog: replay.orderLog, ruleset: replay.ruleset,
    });
    useStore.getState().setStage('debrief');
    clearFightParam();
    onResolved();
  }

  function fightYourself(): void {
    if (!record || busy) return;
    setBusy('plan');
    const store = useStore.getState();
    for (const order of record.plan) store.addPlanOrder(order);
    store.beginPlanning();
    clearFightParam();
    onResolved();
  }

  return (
    <div data-testid="fight-landing" className="flex h-full items-center justify-center bg-ink px-6">
      <div className="w-full max-w-[520px] rounded-sm border border-slate-700 bg-slate-900/60 p-6">
        <Wordmark size="page" suffix="shared fight" />
        {loadError && !record && (
          <>
            <p className="mt-4 text-[13px] text-fog" data-testid="fight-landing-error">{loadError}</p>
            <button
              type="button"
              onClick={() => { clearFightParam(); onResolved(); }}
              className="mono mt-4 rounded-sm border border-slate-700 px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] text-paper"
            >
              Back to the scenario
            </button>
          </>
        )}
        {record && (
          <>
            <p className="mt-3 text-[13px] leading-relaxed text-fog">
              Somebody sent you a fight on <span className="text-paper">{record.ao.toUpperCase()}</span>:
              a run that ended at{' '}
              <span className="text-paper">H+{Math.floor(record.endedTick / 10 / 60)
                .toString().padStart(2, '0')}:{Math.floor((record.endedTick / 10) % 60).toString().padStart(2, '0')}</span>,
              {' '}{record.orderLog.length} orders on the net.
            </p>
            <p className="mono mt-3 text-[11px] uppercase tracking-[0.15em]" data-testid="fight-verify">
              {verify === 'checking' && <span className="text-fog">Reconstructing the run to verify it…</span>}
              {verify === 'identical' && (
                <span style={{ color: 'var(--color-friendly)' }}>Verified identical — the replay reproduces the recorded run exactly.</span>
              )}
              {verify === 'diverged' && (
                <span style={{ color: 'var(--color-warn)' }}>
                  Diverged — this replay did not reproduce the sender&apos;s hash{loadError ? ` (${loadError})` : ''}.
                </span>
              )}
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <button
                type="button"
                data-testid="watch-replay"
                disabled={!replay || busy !== null}
                onClick={watchReplay}
                className="mono rounded-sm border px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] disabled:opacity-40"
                style={{
                  borderColor: 'var(--color-friendly)', color: 'var(--color-friendly)',
                  background: 'color-mix(in srgb, var(--color-friendly) 10%, transparent)',
                }}
              >
                {replay ? 'Watch the replay' : 'Reconstructing…'}
              </button>
              <button
                type="button"
                data-testid="fight-yourself"
                disabled={busy !== null}
                onClick={fightYourself}
                className="mono rounded-sm border border-slate-700 px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] text-paper disabled:opacity-40"
              >
                Fight it yourself
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
